import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  ViewChild
} from "@angular/core";
import { AdvAnnouncementService } from "@onsip/common/services/api/resources/announcement/adv-announcement.service";
import { NonNullableFormBuilder, Validators } from "@angular/forms";
import { BehaviorSubject, Subscription } from "rxjs";
import { SnackbarService } from "../../../../../shared/components/snackbar/snackbar.service";
import { TranslateService } from "@ngx-translate/core";
import { take } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { ModalMaterialComponent } from "../../../../../shared/components/modal/modal-material.component";
import {
  ANNOUNCEMENTS_HANGUP_AOR,
  ANNOUNCEMENTS_HANGUP_OPTIONS
} from "../../../../addressAutocomplete/helpers/generate-announcements.config";
import { AdvAnnouncementWithExt } from "@onsip/web/features/administrators/announcements/announcementsPage/adv-announcements-page.component";
import { ExtensionsListComponent } from "@onsip/web/features/shared/components/extensionsList/extensions-list.component";
import { SipAddressFieldComponent } from "@onsip/web/features/shared/components/sipAddressField/sip-address-field.component";
import { E2eLocators } from "@onsip/e2e/configs/locators";
import { NAME_VALIDATOR_REGEX } from "@onsip/common/libraries/validators/name-validators";

@Component({
  selector: "onsip-adv-announcements-basic-edit",
  templateUrl: "./adv-announcements-basic-edit.component.html",
  styleUrls: ["./adv-announcements-basic-edit.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AdvAnnouncementsBasicEditComponent implements OnInit, OnDestroy {
  @Input() announcement!: AdvAnnouncementWithExt;
  @Output() viewPanel = new EventEmitter<void>();
  @ViewChild("extensionsList") extensionsListRef!: ExtensionsListComponent;
  @ViewChild("sipAddress") sipAddressRef!: SipAddressFieldComponent;

  announcementInfo = this.fb.group({
    announcementName: [
      "",
      [Validators.maxLength(60), Validators.pattern(NAME_VALIDATOR_REGEX), Validators.required]
    ],
    transferUsername: [""],
    sipAddress: [""],
    extensions: [<Array<string>>[]]
  });
  customOptions = ANNOUNCEMENTS_HANGUP_OPTIONS;
  E2eLocators = E2eLocators;
  loadingStateComplete$ = new BehaviorSubject(true);

  private unsubscriber = new Subscription();

  constructor(
    private announcementService: AdvAnnouncementService,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private fb: NonNullableFormBuilder
  ) {}

  ngOnInit() {
    this.announcementInfo.patchValue({
      announcementName: this.announcement?.name,
      transferUsername: this.announcement.transferAddress || ANNOUNCEMENTS_HANGUP_AOR,
      sipAddress: this.announcement.appAddress.address.username,
      extensions: this.announcement.extensions
    });
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  togglePanel(): void {
    this.unsubscriber.add(
      this.dialog
        .open(ModalMaterialComponent, {
          panelClass: ["mat-typography", "onsip-dialog-universal-style"],
          data: {
            title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
            message: this.translate.instant(
              "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
            ),
            primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
            secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
            primaryBtnFlat: true
          }
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(res => {
          if (res && res.doPrimaryAction) this.viewPanel.emit();
        })
    );
  }

  getAnnouncementNameError(): string {
    const announcementName = this.announcementInfo.controls.announcementName;
    return announcementName.hasError("maxlength")
      ? this.translate.instant("ONSIP_I18N.MUST_BE_LESS_THAN_60_CHARACTERS")
      : announcementName.hasError("pattern")
      ? this.translate.instant(
          "ONSIP_I18N.MUST_CONTAIN_ONLY_ALPHANUMERIC_CHARACTERS_SPACES_AND_NO_PLUS_COMMA_COLON"
        )
      : announcementName.hasError("required")
      ? this.translate.instant("ONSIP_I18N.NAME_IS_REQUIRED")
      : "";
  }

  showSaveSnackbar(): void {
    this.snackbarService.openSnackBar(
      this.translate.instant("ONSIP_I18N.YOUR_CHANGES_HAVE_BEEN_SAVED"),
      "success"
    );
  }

  handleSave(): void {
    this.loadingStateComplete$.next(false);
    this.announcementService
      .advAnnouncementEdit({
        AdvAnnouncementId: this.announcement.advAnnouncementId,
        Name: this.announcementInfo.controls.announcementName.value,
        RecordingNumber: this.announcement.recordingNumber,
        TransferAddressUsername: this.announcementInfo.controls.transferUsername.value.substring(
          0,
          this.announcementInfo.controls.transferUsername.value.indexOf("@")
        )
      })
      .then(result => {
        if (result.status === "success") {
          const currentAor =
            this.announcement.appAddress.address.username +
            "@" +
            this.announcement.appAddress.address.domain;
          const newAor = `${this.announcementInfo.controls.sipAddress.value}@${this.announcement.appAddress.address.domain}`;
          if (currentAor !== newAor) this.sipAddressRef.updateAppAddress(currentAor);
          this.extensionsListRef.editExtensions(this.announcementInfo.controls.sipAddress.value);

          this.showSaveSnackbar();
          this.viewPanel.emit();
        } else {
          this.snackbarService.openSnackBar(result.data.message, "error");
          this.announcementService.clearErrors();
        }
        this.loadingStateComplete$.next(true);
      });
  }
}
